import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { selectAdminToken } from '../../admin/sessionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { apiUrl } from '../../utils/apiUrl';
import { s3ThemeAssetUrl } from '../../utils/themeAssets';
import '../game/game.css';
import './feedback.css';

const PUBLIC = (process.env.PUBLIC_URL || '').replace(/\/$/, '');

const FEEDBACK_OPTIONS = [
  { id: 'meh', label: 'Meh...', image: `${PUBLIC}/img/meh.png` },
  { id: 'fine', label: 'It was fine', image: `${PUBLIC}/img/it-was-fine.png` },
  { id: 'fun', label: 'Fun!', image: `${PUBLIC}/img/fun.png` },
  { id: 'loved', label: 'Loved it!', image: `${PUBLIC}/img/loved-it.png` },
  { id: 'amazing', label: 'Absolute banger!', image: `${PUBLIC}/img/amazing.png` },
];

const CONFETTI_MS = 2000;
const CONFETTI_COUNT = 64;

function buildImageConfettiPieces() {
  const ts = Date.now();
  return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    key: `${ts}-${i}`,
    x: Math.random() * 92 + 4,
    y: Math.random() * 85 + 5,
    dx: (Math.random() - 0.5) * 520,
    dy: (Math.random() - 0.5) * 600 + (Math.random() * 120),
    rot: Math.random() * 1080 - 540,
    delay: Math.random() * 0.45,
    size: 36 + Math.floor(Math.random() * 44),
  }));
}

export default function Feedback() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { data: themeData, status: themeStatus } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const [isMobile, setIsMobile] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackStatusLoaded, setFeedbackStatusLoaded] = useState(false);
  const [hadSavedFeedback, setHadSavedFeedback] = useState(false);
  const [confettiOn, setConfettiOn] = useState(false);
  const [confettiSrc, setConfettiSrc] = useState('');
  const [confettiPieces, setConfettiPieces] = useState(null);

  useEffect(() => {
    const mqNarrow = window.matchMedia('(max-width: 768px)');
    const mqShortLandscape = window.matchMedia('(max-height: 480px) and (orientation: landscape)');
    const update = () => setIsMobile(mqNarrow.matches || mqShortLandscape.matches);
    update();
    const add = (m) => (m.addEventListener ? m.addEventListener('change', update) : m.addListener(update));
    const rem = (m) => (m.removeEventListener ? m.removeEventListener('change', update) : m.removeListener(update));
    add(mqNarrow);
    add(mqShortLandscape);
    return () => {
      rem(mqNarrow);
      rem(mqShortLandscape);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const base =
      (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '') || window.location.origin;
    dispatch(setBackButtonUrl(user.backButtonRedirect || base));
  }, [dispatch, user]);

  useEffect(() => {
    if (!user) return;
    if (!adminToken) {
      setFeedbackStatusLoaded(true);
      return;
    }
    let cancelled = false;
    const labels = FEEDBACK_OPTIONS.map((o) => o.label);
    (async () => {
      try {
        const resp = await fetch(apiUrl('/spotGameStatus'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const raw = data.userFeedback;
        if (data.success && raw && labels.includes(String(raw).trim())) {
          setSelected(String(raw).trim());
          setHadSavedFeedback(true);
        }
      } catch (e) {
        console.error('spotGameStatus (feedback)', e);
      } finally {
        if (!cancelled) setFeedbackStatusLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  if (!user) {
    navigate('/login', { replace: true });
    return null;
  }

  const sceneBg = isMobile
    ? (themeData?.background_mob ? `url(${s3ThemeAssetUrl(themeData.background_mob)})` : 'none')
    : (themeData?.background_desk ? `url(${s3ThemeAssetUrl(themeData.background_desk)})` : 'none');

  const saveFeedback = async (label) => {
    if (!adminToken || submitting) return;
    setSubmitting(true);
    setSelected(label);
    try {
      const resp = await fetch(apiUrl('/saveUserFeedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user.userId,
          sessionId: user.sessionId,
          organizationId: user.organizationId,
          userFeedback: label,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.success === false) {
        await Swal.fire({
          icon: 'error',
          title: 'Could not save',
          text: data.error || resp.statusText || 'Try again later',
        });
        setSelected(null);
        setSubmitting(false);
        return;
      }

      setHadSavedFeedback(true);
      const opt = FEEDBACK_OPTIONS.find((o) => o.label === label);
      const imgSrc = opt?.image || '';
      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (imgSrc && !reduceMotion) {
        setConfettiSrc(imgSrc);
        setConfettiPieces(buildImageConfettiPieces());
        setConfettiOn(true);
      }

      const base =
        (process.env.REACT_APP_BASE_URL || '').trim().replace(/\/+$/, '') || window.location.origin;
      dispatch(setBackButtonUrl(user.backButtonRedirect || base));

      window.setTimeout(() => {
        setConfettiOn(false);
        setConfettiPieces(null);
        setConfettiSrc('');
        navigate('/leaderboard', { replace: true, state: { feedbackSubmitted: true } });
      }, CONFETTI_MS);
    } catch (e) {
      console.error('saveUserFeedback', e);
      await Swal.fire({ icon: 'error', title: 'Network error', text: String(e.message) });
      setSelected(null);
      setSubmitting(false);
    }
  };

  if (themeStatus !== 'succeeded' || !themeData || !feedbackStatusLoaded) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div
      className={`tot-scene fb-scene${isMobile ? ' is-mobile' : ''}`}
      style={{ backgroundImage: sceneBg }}
      aria-label="Feedback"
    >
      <h1 className="fb-headline">That&rsquo;s a wrap! 🎬</h1>
      <p className="fb-subtext">How was your experience?</p>

      {hadSavedFeedback && (
        <p className="fb-hint">You already sent feedback — tap an option to update it.</p>
      )}

      <section className="fb-options" aria-label="Feedback options">
        {FEEDBACK_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`fb-option${selected === opt.label ? ' fb-option--selected' : ''}`}
            disabled={submitting}
            onClick={() => saveFeedback(opt.label)}
          >
            <span className="fb-option-ring">
              <img src={opt.image} alt="" className="fb-option-img" draggable={false} />
            </span>
            <span className="fb-option-label">{opt.label}</span>
          </button>
        ))}
      </section>

      {confettiOn && confettiSrc && confettiPieces && (
        <div className="fb-confetti" aria-hidden="true">
          {confettiPieces.map((p) => (
            <img
              key={p.key}
              src={confettiSrc}
              alt=""
              className="fb-confetti-img"
              draggable={false}
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: p.size,
                height: p.size,
                '--fb-dx': `${p.dx}px`,
                '--fb-dy': `${p.dy}px`,
                '--fb-rot': `${p.rot}deg`,
                animationDelay: `${p.delay}s`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
